import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

interface PageSeo {
  title: string;
  description: string;
  keywords: string;
}

export function usePageSeo(pageSlug: string, fallback?: Partial<PageSeo>) {
  const [seo, setSeo] = useState<PageSeo | null>(null);

  useEffect(() => {
    const fetchSeo = async () => {
      const { data } = await supabase
        .from("page_seo")
        .select("title, description, keywords")
        .eq("page_slug", pageSlug)
        .maybeSingle();

      if (data) {
        setSeo(data);
        // Apply to document head
        if (data.title) document.title = data.title;

        const setMeta = (name: string, content: string, isProperty = false) => {
          if (!content) return;
          const attr = isProperty ? "property" : "name";
          let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
          if (!el) {
            el = document.createElement("meta");
            el.setAttribute(attr, name);
            document.head.appendChild(el);
          }
          el.content = content;
        };

        setMeta("description", data.description);
        setMeta("keywords", data.keywords);
        setMeta("og:title", data.title, true);
        setMeta("og:description", data.description, true);
        setMeta("twitter:title", data.title);
        setMeta("twitter:description", data.description);

        // Canonical URL
        const baseUrl = "https://lkntechnologies.com";
        const path = pageSlug === "home" ? "/" : `/${pageSlug}`;
        const canonicalUrl = `${baseUrl}${path}`;

        let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
        if (!canonical) {
          canonical = document.createElement("link");
          canonical.rel = "canonical";
          document.head.appendChild(canonical);
        }
        canonical.href = canonicalUrl;

        setMeta("og:url", canonicalUrl, true);

        // Reset image tags to default when navigating away from blog posts
        const defaultImage = "https://lkntechnologies.com/LKNTechLogo.webp";
        setMeta("og:image", defaultImage, true);
        setMeta("twitter:image", defaultImage);
        setMeta("twitter:card", "summary");
        setMeta("og:site_name", "Lake Norman Technologies", true);
      } else if (fallback) {
        if (fallback.title) document.title = fallback.title;
      }
    };

    fetchSeo();
  }, [pageSlug]);

  return seo;
}
